"use client";

import clsx from "clsx";
import Link from "next/link";
import React from "react";
import Button from "./Button";
import CommonCard from "./CommonCard";
import SectionTitle from "./SectionTitle";

type CardData = {
  number?: string | number;
  title?: string;
  description?: string;
  highlight?: string;
  iconSrc?: string;
  iconAlt?: string;
  contentAlign?: "left" | "center" | "right";
};

type MutiPurposeZigZagSectionProps = {
  sectionTitle?: string;
  sectionSubtitle?: string;
  sectionDescription?: string;
  cards?: CardData[];
  buttonText?: string;
  buttonOnClick?: () => void;
  columns?: number;
  rows?: number;
  forceSix?: boolean;
  sixToThree?: boolean;
  buttonLink?: string;
};


const CardSection: React.FC<MutiPurposeZigZagSectionProps> = ({
  sectionTitle,
  sectionSubtitle,
  sectionDescription,
  cards = [],
  buttonText,
  buttonOnClick,
  columns = 3,
  rows,
  forceSix = false,
  sixToThree = false,
  buttonLink,
}) => {
  const effectiveColumns = forceSix ? 6 : columns;
  const allCards = cards;
  const totalRows = rows || Math.ceil(allCards.length / effectiveColumns);

  const gridColsClass = clsx(
    "grid",
    "grid-cols-1",
    effectiveColumns === 1 && "sm:grid-cols-1",
    effectiveColumns === 2 && "sm:grid-cols-2",
    effectiveColumns === 3 && "sm:grid-cols-3",
    effectiveColumns === 4 && "sm:grid-cols-4",
    effectiveColumns === 5 && "sm:grid-cols-5",
    effectiveColumns === 6 && "sm:grid-cols-6"
  );

  return (
    <section className="custom-container ">
      <div className="">
        {(sectionTitle || sectionSubtitle || sectionDescription) && (
          <SectionTitle
            title={sectionTitle || ""}
            subtitle={sectionSubtitle || ""}
            description={sectionDescription || ""}
            align="center"
          />
        )}

        {/* --- First Grid: XL+ with zig-zag lift --- */}
        <div className="hidden xl:block mt-8 mt-16">
          {(() => {
            if (allCards.length === 7 || allCards.length === 8) {
              const splitConfig = allCards.length === 7 ? [2, 3, 2] : [3, 2, 3];
              let pointer = 0;
              const maxColumnLength = Math.max(...splitConfig);

              return (
                <div className="grid grid-cols-3 lg:gap-8 gap-4 items-stretch">
                  {splitConfig.map((count, colIndex) => {
                    const colCards = allCards.slice(pointer, pointer + count);
                    pointer += count;
                    const isShortColumn = count < maxColumnLength;

                    return (
                      <div
                        key={colIndex}
                        className={clsx(
                          "flex flex-col lg:gap-8 gap-4",
                          isShortColumn ? "justify-center" : "justify-start"
                        )}
                      >
                        {colCards.map((card, idx) => {
                          const isEmpty =
                            !card.number &&
                            (!card.title || card.title.trim() === "") &&
                            (!card.description || card.description.trim() === "") &&
                            (!card.highlight || card.highlight.trim() === "") &&
                            !card.iconSrc;

                          return (
                            <div
                              key={idx}
                              className="relative"
                            >
                              <div className="h-full text-center flex">
                                {isEmpty ? (
                                  <div className="w-full h-full min-h-[200px]"></div>
                                ) : (
                                  <CommonCard
                                    {...(card.number && { number: card.number })}
                                    title={card.title || ""}
                                    {...(card.description && { description: card.description })}
                                    {...(card.highlight && { highlight: card.highlight })}
                                    {...(card.iconSrc && { iconSrc: card.iconSrc })}
                                    {...(card.iconAlt && { iconAlt: card.iconAlt })}
                                    {...(card.contentAlign && { contentAlign: card.contentAlign })}
                                  />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            }

            return Array.from({ length: totalRows }).map((_, rowIndex) => {
              const start = rowIndex * effectiveColumns;
              const end = start + effectiveColumns;
              const rowCards = allCards.slice(start, end);

              return (
                <div
                  key={rowIndex}
                  className={clsx(gridColsClass, "lg:gap-8 gap-4 mb-8 items-stretch")}
                >
                  {rowCards.map((card, colIndex) => {
                    const liftClass = colIndex % 2 === 1 ? "-translate-y-10" : "";
                    const isEmpty =
                      !card.number &&
                      (!card.title || card.title.trim() === "") &&
                      (!card.description || card.description.trim() === "") &&
                      (!card.highlight || card.highlight.trim() === "") &&
                      !card.iconSrc;

                    return (
                      <div
                        key={colIndex}
                        className={clsx("relative", liftClass)}
                      >
                        <div className="h-full text-center flex">
                          {isEmpty ? (
                            <div className="w-full h-full min-h-[200px]"></div>
                          ) : (
                            <CommonCard
                              {...(card.number && { number: card.number })}
                              title={card.title || ""}
                              {...(card.description && { description: card.description })}
                              {...(card.highlight && { highlight: card.highlight })}
                              {...(card.iconSrc && { iconSrc: card.iconSrc })}
                              {...(card.iconAlt && { iconAlt: card.iconAlt })}
                              {...(card.contentAlign && { contentAlign: card.contentAlign })}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            });
          })()}
        </div>

        {/* --- Responsive Below XL --- */}
        <div className="hidden sm:block xl:hidden mt-8 lg:mt-32 mx-auto">
          {(() => {
            const columnsBelowXL = sixToThree && effectiveColumns > 2 ? 2 : effectiveColumns;
            const rowsBelowXL = Math.ceil(allCards.length / columnsBelowXL);

            return Array.from({ length: rowsBelowXL }).map((_, rowIndex) => {
              const start = rowIndex * columnsBelowXL;
              const end = start + columnsBelowXL;
              const rowCards = allCards.slice(start, end);

              return (
                <div
                  key={rowIndex}
                  className={clsx(
                    "grid",
                    `grid-cols-${columnsBelowXL}`,
                    "lg:gap-8 gap-4 mb-8 items-stretch"
                  )}
                >
                  {rowCards.map((card, colIndex) => {
                    const isEmpty =
                      !card.number &&
                      (!card.title || card.title.trim() === "") &&
                      (!card.description || card.description.trim() === "") &&
                      (!card.highlight || card.highlight.trim() === "") &&
                      !card.iconSrc;

                    return (
                      <div key={colIndex} className="relative">
                        <div className="h-full text-center flex">
                          {isEmpty ? (
                            <div className="w-full h-full min-h-[200px]"></div>
                          ) : (
                            <CommonCard
                              {...(card.number && { number: card.number })}
                              title={card.title || ""}
                              {...(card.description && { description: card.description })}
                              {...(card.highlight && { highlight: card.highlight })}
                              {...(card.iconSrc && { iconSrc: card.iconSrc })}
                              {...(card.iconAlt && { iconAlt: card.iconAlt })}
                              {...(card.contentAlign && { contentAlign: card.contentAlign })}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            });
          })()}
        </div>

        {/* --- Single-column below SM --- */}
        <div className="block sm:hidden mt-8 mx-auto">
          {Array.from({ length: totalRows }).map((_, rowIndex) => {
            const start = rowIndex * effectiveColumns;
            const end = start + effectiveColumns;
            const rowCards = allCards.slice(start, end);

            return (
              <div
                key={rowIndex}
                className={clsx("grid grid-cols-1 lg:gap-8 gap-4 mb-8 items-stretch")}
              >
                {rowCards.map((card, colIndex) => {
                  const isEmpty =
                    !card.number &&
                    (!card.title || card.title.trim() === "") &&
                    (!card.description || card.description.trim() === "") &&
                    (!card.highlight || card.highlight.trim() === "") &&
                    !card.iconSrc;

                  return (
                    <div key={colIndex} className="relative">
                      <div className="h-full text-center flex">
                        {isEmpty ? (
                          <div className="w-full h-full min-h-[200px]"></div>
                        ) : (
                          <CommonCard
                            {...(card.number && { number: card.number })}
                            title={card.title || ""}
                            {...(card.description && { description: card.description })}
                            {...(card.highlight && { highlight: card.highlight })}
                            {...(card.iconSrc && { iconSrc: card.iconSrc })}
                            {...(card.iconAlt && { iconAlt: card.iconAlt })}
                            {...(card.contentAlign && { contentAlign: card.contentAlign })}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {buttonText && (
          <div className="mx-auto text-center mt-8 lg:mt-16">
            <Link href={buttonLink || '#'}>
              <Button
                responsiveSize={{ mobile: "md", tablet: "md", desktop: "lg" }}
                variant="primary"
                {...(buttonOnClick && { onClick: buttonOnClick })}
                className="lg:text-lg"
              >
                {buttonText}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default CardSection;
